/**
 * FadeTransition provides a JavaScript-driven fade transition effect between views.
 * It is implemented using React state and animations (via styled-components and GLOBAL_KEYFRAMES).
 * This component serves as a fallback for browsers that do not support the native View Transitions API,
 * or in scenarios where more granular control over the transition lifecycle and animations is required,
 * or when CSS-based animations are preferred over native browser transitions.
 * It offers features like debouncing view updates and controlling initial entry animation.
 */
// eslint-disable-next-line import/no-extraneous-dependencies, monorepo/no-internal-import
import {useIsomorphicLayoutEffect} from '@aboutyou/ui-components/src/hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';
// eslint-disable-next-line monorepo/no-internal-import
import {GLOBAL_KEYFRAMES} from '@aboutyou/ui-components/src/components/atoms/BaseStyles/keyFrames';
import {doAsync, usePrevious} from '@aboutyou/utils';
import {styled} from '@linaria/react';
// eslint-disable-next-line import/no-extraneous-dependencies
import debounce from 'lodash.debounce';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

type FadeTransitionProps = {
  children: React.ReactNode;
  disabled?: boolean;
  duration: number;
  debounceWait?: number;
  animateInitialEntry?: boolean;
  applyChangeViewsImmediately?: boolean;
  className?: string;
  // The uniqueKey allows external control over when the next view should be mounted.
  // It ensures that the next view is mounted only when uniqueKey changes, for example, when the data for the next view is ready.
  uniqueKey?: string;
};

export const FadeTransition = ({
  children,
  disabled,
  className,
  duration,
  animateInitialEntry = false,
  applyChangeViewsImmediately = true,
  debounceWait = duration * 2,
  uniqueKey,
  ref,
}: FadeTransitionProps & {ref?: React.Ref<HTMLDivElement>}) => {
  const nextView = React.useMemo(() => children, [children]);
  const previousView = usePrevious(nextView);
  const previousUniqueKey = usePrevious(uniqueKey);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousViewRef = useRef<HTMLDivElement>(null);
  const previousViewToBeRendered = useRef<React.ReactNode>(null);
  const [calculatedHeight, setCalculatedHeight] = useState<number | undefined>(
    undefined,
  );

  // Forward ref to containerRef
  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement, []);

  useEffect(() => {
    const previewView = previousViewRef.current;

    if (previewView) {
      previewView.setAttribute('inert', '');
    }

    return () => {
      if (previewView) {
        previewView.removeAttribute('inert');
      }
    };
  }, [previousViewRef]);

  const [triggerTransitions, setTriggerTransitions] =
    useState(animateInitialEntry);

  const [renderedNextView, setRenderedNextView] =
    useState<React.ReactNode>(nextView);
  const [renderedPreviousView, setRenderedPreviousView] =
    useState<React.ReactNode>(null);

  // Measure and update height after DOM updates
  // HeightHolder has className applied so its offsetHeight includes padding
  useIsomorphicLayoutEffect(() => {
    setCalculatedHeight(placeholderRef.current?.offsetHeight);
  }, [nextView, renderedNextView]);

  const triggerNextViewDebounced = useRef(
    debounce(
      (nextView: React.ReactNode) => {
        const previousView = previousViewToBeRendered.current;
        previousViewToBeRendered.current = null;
        setRenderedPreviousView(previousView);
        setTriggerTransitions(false);

        doAsync(() => {
          if (placeholderRef.current) {
            setTriggerTransitions(true);
            setRenderedNextView(nextView);
          }
        });
      },
      duration,
      {
        maxWait: debounceWait,
        leading: applyChangeViewsImmediately,
        trailing: true,
      },
    ),
  ).current;

  const getCanTriggerNextView = useCallback(() => {
    // If uniqueKey is provided, we check for changes to determine when to mount the next view.
    if (uniqueKey) {
      if (previousUniqueKey && previousUniqueKey !== uniqueKey) {
        if (!previousViewToBeRendered.current) {
          previousViewToBeRendered.current = previousView;
        }

        if (!disabled) {
          return true;
        }
      }

      return false;
    }

    // Otherwise, we mount the next view directly when the children change.
    if (previousView && previousView !== nextView) {
      if (!previousViewToBeRendered.current) {
        previousViewToBeRendered.current = previousView;
      }

      if (!disabled) {
        return true;
      }
    }

    return false;
  }, [disabled, nextView, previousUniqueKey, previousView, uniqueKey]);

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (getCanTriggerNextView()) {
      triggerNextViewDebounced(nextView);
    }
  }, [getCanTriggerNextView, nextView, triggerNextViewDebounced]);

  const [prevViewZIndex, nextViewZIndex] = useMemo(() => {
    if (getCanTriggerNextView()) {
      return triggerTransitions ? [1, 0] : [0, 1];
    }

    return [1, 0];
  }, [getCanTriggerNextView, triggerTransitions]);

  return (
    <Container
      className={className}
      $duration={duration}
      $height={calculatedHeight}
      ref={containerRef}
    >
      {nextView && (
        <AbsolutePositioned
          data-view-state={TransitionState.BeforeTransition}
          style={{
            opacity: triggerTransitions ? 1 : 0,
            zIndex: nextViewZIndex,
            animation: triggerTransitions
              ? `${GLOBAL_KEYFRAMES.fadeExit} ${duration}ms ease-in forwards`
              : undefined,
          }}
          ref={previousViewRef}
        >
          {renderedPreviousView}
        </AbsolutePositioned>
      )}

      <AbsolutePositioned
        data-view-state={TransitionState.FinalView}
        style={{
          opacity: triggerTransitions ? 0 : 1,
          zIndex: prevViewZIndex,
          animation: triggerTransitions
            ? `${GLOBAL_KEYFRAMES.fadeEnter} ${duration}ms ease-in forwards`
            : undefined,
        }}
      >
        {renderedNextView}
      </AbsolutePositioned>

      <HeightHolder ref={placeholderRef} className={className}>
        {nextView}
      </HeightHolder>
    </Container>
  );
};

const Container = styled.div<{$height?: number; $duration: number}>`
  position: relative;
  width: 100%;
  box-sizing: border-box;

  // eslint-disable-next-line local-rules/css-layout-animation
  transition: height ${({$duration}) => $duration}ms ease-in-out;
  height: ${({$height}) => ($height ? `${$height}px` : 'auto')};
`;

const AbsolutePositioned = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  will-change: opacity;
`;

const HeightHolder = styled.div`
  opacity: 0;
  pointer-events: none;
`;

export enum TransitionState {
  BeforeTransition = 'data-previous-view',
  FinalView = 'data-final-view',
}
